import {makeAutoObservable} from "mobx";
import {UniqueIdentifier} from "@dnd-kit/core";
import {DocumentAgent} from "../agent/document-agent";
import {RequestBody} from "../../entity/core/request-body";
import {Title} from "../../entity/model/title";
import {Document} from "../../entity/resource/document";
import {DocumentRunnerDto} from "../../entity/dto/document-runner-dto";
import {ValidateResponse} from "../../entity/resource/validate-response";
import {SplitAIDto} from "../../entity/dto/split-ai-dto";
import {Section} from "../../entity/resource/section";
import {TitleTemplate} from "../../entity/model/title-template";
import {SignedUrlResource} from "../../entity/resource/signed-url-resource";
import {AxiosResponse} from "axios";
import {SplitDocumentRequest} from "../../entity/resource/split-document-request";


export default class DocumentStore {
  documentAgent: DocumentAgent;
  isDocumentMoving: boolean = false;
  movingDocumentId: UniqueIdentifier | string = '';
  document?: Document;
  section?: Section;
  splitRequests: Map<string, SplitDocumentRequest[]> = new Map();

  constructor(documentAgent: DocumentAgent) {
    makeAutoObservable(this);
    this.documentAgent = documentAgent;
  }

  moveDocument = async (organizationId: string, sectionId: UniqueIdentifier, requestBody: RequestBody, titleId?: string, templateId?: string): Promise<Title | TitleTemplate | string> => {
    try {
      return this.documentAgent.moveDocument(organizationId, sectionId, requestBody, titleId, templateId);
    } catch (error: any) {
      console.error('An error occurred while moving the document !!', error);
      return 'An error occurred while moving the document !!';
    }
  }

  deleteDocument = async (organizationId: string, sectionId: string, documentId: UniqueIdentifier, titleId?: string, templateId?: string): Promise<string | undefined> => {
    try {
      await this.documentAgent.deleteDocument(organizationId, sectionId, documentId, titleId, templateId);
    } catch (error: any) {
      console.error('An error occurred while deleting the document !!', error);
      return 'An error occurred while deleting the document !!';
    }
  }

  updateDocument = async (organizationId: string, sectionId: string, documentId: string, requestBody: RequestBody, titleId?: string, templateId?: string): Promise<string | undefined> => {
    try {
      await this.documentAgent.updateDocument(organizationId, sectionId, documentId, requestBody,titleId, templateId);
    } catch (error: any) {
      console.error('An error occurred while updating the document !!', error);
      return 'An error occurred while updating the document !!';
    }
  }

  getPreSignedUrl = async (organizationId: string, requestBody: RequestBody, titleId?: string, titleTemplateId?: string): Promise<SignedUrlResource | string> => {
    try {
      return titleId
          ? await this.documentAgent.getPreSignedUrl(organizationId, titleId, requestBody)
          : await this.documentAgent.getSignedUrl(organizationId, titleTemplateId ?? '', requestBody);
    } catch (error: any) {
      console.error('An error occurred while uploading the document !!', error);
      return 'An error occurred while uploading the document !!';
    }
  }

  s3FileUpload = async (file: Blob, url: string, setFileProgress?: (progress: number) => void): Promise<AxiosResponse<any> | string> => {
    try {
      return await this.documentAgent.s3FileUpload(file, url, setFileProgress);
    } catch (error: any) {
      console.error('An error occurred while uploading the document !!', error);
      return 'An error occurred while uploading the document !!';
    }
  }

  uploadDocument = async (organizationId: string, titleId: string, requestBody: RequestBody, sectionId?: string): Promise<any> => {
    try {
      return await this.documentAgent.uploadDocument(organizationId, titleId, requestBody, sectionId);
    } catch (error: any) {
      console.error('An error occurred while uploading the document !!', error);
      return 'An error occurred while uploading the document !!';
    }
  }

  createMergePdf = async (organizationId: string, titleId: string, documentIds: string[], batchID: string): Promise<any> => {
    try {
      return await this.documentAgent.createMergePdf(organizationId, titleId, documentIds, batchID);
    } catch (error: any) {
      console.error('An error occurred while merging the document. Please try again in few minutes!', error);
    }
  }

  validateDocuments = async (organizationId: string, titleId: string, documentIDs: string[], batchId: string): Promise<ValidateResponse | string> => {
    try {
      return await this.documentAgent.validateDocuments(organizationId, titleId, documentIDs, batchId);
    } catch (error: any) {
      console.error('An error occurred while validating the documents !!', error);
      return 'An error occurred while validating the documents !!';
    }
  }

  splitDocument = async (organizationId: string, titleId: string, requestBody: RequestBody): Promise<any> => {
    try {
      return await this.documentAgent.splitDocument(organizationId, titleId, requestBody);
    } catch (error: any) {
      console.error('Error occurred while split document. Please try again in while !!', error);
      return 'Error occurred while split document. Please try again in while !!'
    }
  }

  documentRunner = async (organizationId: string, webScraperDto: DocumentRunnerDto): Promise<any> => {
    try {
      const requestBody = {body: webScraperDto};
      return await this.documentAgent.documentRunner(organizationId, requestBody);
    } catch (error: any) {
      console.error('Error occurred while execute document runner. Please try again in while !!', error);
      return 'Error occurred while execute document runner. Please try again in while !!';
    }
  }

  splitWithAI = async (requestBody: SplitAIDto): Promise<any> => {
    try {
      return await this.documentAgent.splitWithAI(requestBody);
    } catch (error) {
      console.error('Error occurred while split document. Please try again in while !!', error);
      return 'Error occurred while split document. Please try again in while !!'
    }
  }

  publishDocument = async (organizationId: string, titleTemplateId: string, titleId: string, requestBody: RequestBody): Promise<any> => {
    try {
      return await this.documentAgent.publishDocument(organizationId, titleTemplateId, titleId, requestBody);
    } catch (error) {
      console.error('Error occurred while publish document. Please try again in while !!', error);
      return 'Error occurred while publish document. Please try again in while !!';
    }
  }
}
